"use client";

import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  ERROR_NOTICE,
  HINT,
  INPUT,
  INPUT_MONO,
  LABEL,
  NOTICE,
  PILL,
  SELECT,
  TABLE,
  TD,
  TEXTAREA,
  TEXTAREA_PROSE,
  TH,
} from "@/components/tool-kit/styles";
import { copyRichText, writeToClipboard } from "@/lib/clipboard";

/*
 * Shared building blocks for family-built tool widgets.
 *
 * Every visible word comes in through props. These components own layout and
 * behaviour, never vocabulary: a widget passes its own `strings.*` values, which is what
 * lets a translated content file reach every label — a default English word inside a
 * shared component would be the one string on the page no translation could touch.
 */

export function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label className={LABEL} htmlFor={id}>
        {label}
      </label>
      {children}
      {hint ? <p className={HINT}>{hint}</p> : null}
    </div>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  mono = false,
  numeric = false,
  type = "text",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: ReactNode;
  mono?: boolean;
  /** Opens the decimal keypad on phones. The value is still text: parse it yourself. */
  numeric?: boolean;
  type?: "text" | "date" | "time" | "url" | "datetime-local" | "search";
}) {
  return (
    <Field hint={hint} id={id} label={label}>
      <input
        className={mono || numeric ? INPUT_MONO : INPUT}
        id={id}
        inputMode={numeric ? "decimal" : undefined}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type={type}
        value={value}
      />
    </Field>
  );
}

export function TextAreaField({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  prose = false,
  rows,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: ReactNode;
  /** Proportional type for sentences; the default monospace suits data and code. */
  prose?: boolean;
  rows?: number;
}) {
  return (
    <Field hint={hint} id={id} label={label}>
      <textarea
        className={prose ? TEXTAREA_PROSE : TEXTAREA}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        value={value}
      />
    </Field>
  );
}

export interface Option<V extends string = string> {
  value: V;
  label: string;
}

export function SelectField<V extends string>({
  id,
  label,
  value,
  onChange,
  options,
  hint,
}: {
  id: string;
  label: string;
  value: V;
  onChange: (value: V) => void;
  options: readonly Option<V>[];
  hint?: ReactNode;
}) {
  return (
    <Field hint={hint} id={id} label={label}>
      <select
        className={SELECT}
        id={id}
        onChange={(event) => onChange(event.target.value as V)}
        value={value}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
      <input
        checked={checked}
        className="accent-primary"
        onChange={(event) => onChange(event.target.checked)}
        type="checkbox"
      />
      {label}
    </label>
  );
}

/** A row of mutually exclusive choices drawn as pills — mode and style switches. */
export function PillGroup<V extends string>({
  name,
  label,
  value,
  onChange,
  options,
}: {
  /** The radio group's `name`; must be unique on the page. */
  name: string;
  label: string;
  value: V;
  onChange: (value: V) => void;
  options: readonly Option<V>[];
}) {
  return (
    <fieldset className="space-y-3">
      <legend className={LABEL}>{label}</legend>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (
          <label className={PILL} key={option.value}>
            <input
              checked={value === option.value}
              className="accent-primary"
              name={name}
              onChange={() => onChange(option.value)}
              type="radio"
              value={option.value}
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function StatCard({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: ReactNode;
  /** The headline number of a result set. */
  emphasis?: boolean;
}) {
  return (
    <div
      className={
        emphasis
          ? "rounded-lg border border-primary/30 bg-primary/5 p-4 text-center"
          : "rounded-lg border border-border bg-card p-4 text-center"
      }
    >
      <p className="break-words font-semibold text-2xl text-foreground">
        {value}
      </p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export function StatGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">{children}</div>
  );
}

/** "Enter at least two values to see the result" — guidance, not an error. */
export function Notice({ children }: { children: ReactNode }) {
  return <p className={NOTICE}>{children}</p>;
}

export function ErrorNotice({ children }: { children: ReactNode }) {
  return (
    <p className={ERROR_NOTICE} role="alert">
      {children}
    </p>
  );
}

/**
 * A copy button that says "Copied!" for two seconds.
 *
 * Pass `html` as well as `text` to put formatted text on the clipboard — a citation's
 * italics survive the paste into Word or Google Docs, and plain-text targets still get
 * `text`.
 */
export function CopyButton({
  text,
  html,
  label,
  copiedLabel,
  small = false,
  disabled = false,
}: {
  text: string;
  html?: string;
  label: string;
  copiedLabel: string;
  small?: boolean;
  disabled?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  async function handleCopy() {
    if (text.length === 0) {
      return;
    }
    const ok = html
      ? await copyRichText(html, text)
      : await writeToClipboard(text);
    if (!ok) {
      return;
    }
    setCopied(true);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      className={small ? BUTTON_SMALL : BUTTON_SECONDARY}
      disabled={disabled || text.length === 0}
      onClick={handleCopy}
      type="button"
    >
      {copied ? copiedLabel : label}
    </button>
  );
}

/**
 * A simple data table. Cells are text — a widget that needs markup in a cell should
 * build its own table with the `TABLE`/`TH`/`TD` classes instead.
 */
export function DataTable({
  head,
  rows,
  caption,
}: {
  head: readonly string[];
  rows: readonly (readonly (string | number)[])[];
  caption?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        {caption ? (
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {caption}
          </caption>
        ) : null}
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // Rows are positional data with no identity of their own.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                <td className={TD} key={cellIndex}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A bordered box with an optional heading — groups related fields or a preview. */
export function Panel({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={`${CARD} space-y-4`}>
      {title ? <p className={LABEL}>{title}</p> : null}
      {children}
    </div>
  );
}
